import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../Common/Table";
import { AUDIENCES, CampaignRow, CAMPAIGN_ROWS, NOT_SENT } from "../../data";
import StatusBadge from "../../StatusBadge";

/** What the filter row above the table narrows the list by. */
interface CampaignFilters {
  audience?: string;
  status?: CampaignRow["status"];
  /** Both ISO days, from the period picker. */
  sentFrom?: string;
  sentTo?: string;
}

interface CampaignTableProps {
  /**
   * Keys of the campaigns deleted in this session, which the table hides.
   *
   * Deleting is not offered here: a row carries the actions that open the
   * campaign, and the one that ends it is behind More on the two screens that
   * show a campaign whole — the preview drawer and the builder — where the
   * user can see what they are about to lose.
   */
  deleted: string[];
  onCreate: () => void;
  onOpen: (row: CampaignRow, mode: "preview" | "edit" | "copy") => void;
}

export default function CampaignTable({
  deleted,
  onCreate,
  onOpen,
}: CampaignTableProps) {
  const { t } = useTranslation();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  // Mock rows live in the component, so the toolbar search filters them here.
  // Returning false from onSearch keeps ProTable from putting a keyword into
  // the request params — there is no request behind this table yet.
  const [query, setQuery] = useState("");
  // What the filter row above the table is set to. Local, like the search
  // above: the rows are a mock and there is no request for ProTable to put
  // these into.
  const [filters, setFilters] = useState<CampaignFilters>({});

  // TODO(design): once the list comes from an endpoint, all of this is the
  // API's job — drop it and let the search and the filter form fall through to
  // the request.
  const visibleRows = CAMPAIGN_ROWS.filter((row) => {
    const wanted = query.trim().toLowerCase();

    if (deleted.includes(row.key)) return false;

    if (wanted && !row.subject.toLowerCase().includes(wanted)) return false;
    if (filters.audience && row.audience !== filters.audience) return false;
    if (filters.status && row.status !== filters.status) return false;

    if (filters.sentFrom || filters.sentTo) {
      // A draft has never been sent, so it is outside every period rather than
      // inside all of them. The dates are ISO, which compares as text.
      if (!row.at) return false;

      const day = row.at.slice(0, 10);

      if (filters.sentFrom && day < filters.sentFrom) return false;
      if (filters.sentTo && day > filters.sentTo) return false;
    }

    return true;
  });

  const columns: ProColumns<CampaignRow>[] = [
    {
      title: t("pages.campaigns.table.subject"),
      dataIndex: "subject",
      key: "subject",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: t("pages.campaigns.table.audience"),
      dataIndex: "audience",
      key: "audience",
      sorter: true,
      // A list rather than a text field: the audiences are a known set, and
      // typing one out is a way to get it wrong. Keyed by name, which is what
      // the row carries.
      valueType: "select",
      valueEnum: Object.fromEntries(
        AUDIENCES.map(({ name }) => [name, { text: name }]),
      ),
    },
    {
      title: t("pages.campaigns.table.sent"),
      dataIndex: "sent",
      key: "sent",
      sorter: true,
      hideInSearch: true,
      // A draft has sent nothing; a zero there read as a real result rather
      // than as nothing having happened yet.
      render: (_, record) =>
        record.status === "Sent" ? record.sent.toLocaleString() : NOT_SENT,
    },
    {
      title: t("pages.campaigns.table.sentAt"),
      dataIndex: "at",
      key: "at",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => record.at || NOT_SENT,
    },
    {
      // The same column again, as a period. A date column filters by one day
      // and a campaign list is read by the month it went out in, so the filter
      // is a range and lives on a twin the table itself never shows — the
      // pattern the Notifications outbox already uses.
      title: t("pages.campaigns.table.sentAt"),
      dataIndex: "at",
      key: "sentPeriod",
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) =>
          value && value.length === 2
            ? { sentFrom: value[0], sentTo: value[1] }
            : {},
      },
    },
    {
      title: t("pages.campaigns.table.status"),
      dataIndex: "status",
      key: "status",
      sorter: true,
      valueType: "select",
      // The same words the badge shows, from the same keys — the filter used to
      // carry its own untranslated copies of them.
      valueEnum: {
        Draft: { text: t("pages.campaigns.status.Draft") },
        Sent: { text: t("pages.campaigns.status.Sent") },
      },
      width: 210,
      // The cell keeps its badge: a column's own render outranks the valueType,
      // which is only there to say what the filter above offers.
      render: (_, record) => <StatusBadge status={record.status} />,
    },
  ];

  return (
    <Table<CampaignRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className="transparent"
      headerTitle={t("pages.campaigns.tableName")}
      dataSource={visibleRows}
      columns={columns}
      rowKey="key"
      // The filter row above the table. The wrapper supplies its shape — an
      // inline query filter with Filter and Clear — so all this says is that
      // this table has one.
      search={{}}
      onSubmit={(params) => setFilters(params as CampaignFilters)}
      // The wrapper defines an onReset of its own and a screen's wins over it,
      // since props are spread last. What it does there — resetting the table's
      // action ref — is covered by the remount `resetKey` triggers, so the two
      // that matter are repeated here: clear the filters, and remount.
      onReset={() => {
        setFilters({});
        setTableKey(Date.now());
      }}
      toolBarRender={() => [
        <Button
          key="create"
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={onCreate}
        >
          {/* "Create", not "Create campaign": the table it sits on is already
              called List of campaigns, and the word was said twice in one
              row. The page it opens still carries the full name. */}
          {t("pages.campaigns.createShort")}
        </Button>,
      ]}
      actions={[
        [
          "view",
          ({ record }) => ({
            title: t("pages.campaigns.actions.preview"),
            onClick: () => onOpen(record, "preview"),
          }),
        ],
        [
          "edit",
          ({ record }) => ({
            title: t("pages.campaigns.actions.edit"),
            onClick: () => onOpen(record, "edit"),
          }),
          // A sent campaign can be viewed and copied, but not edited.
          ({ record }) => record.status !== "Sent",
        ],
        [
          "copy",
          ({ record }) => ({
            title: t("pages.campaigns.actions.copy"),
            onClick: () => onOpen(record, "copy"),
          }),
        ],
      ]}
      // reload, density and setting come from the shared Table — ProTable
      // already ships the column settings panel the spec describes: reorder,
      // hide, reset.
      options={{
        search: {
          name: "subject",
          onSearch: (keyword: string) => {
            setQuery(keyword);
            return false;
          },
        },
      }}
      pagination={{
        // The pager appears only once the table outgrows a page.
        hideOnSinglePage: true,
        showTotal: (total) =>
          t("pages.campaigns.table.total", { count: total }),
      }}
      columnsState={{ persistenceKey: "pro-table-campaigns" }}
    />
  );
}
